import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MOBILE_BREAKPOINT } from '../../hooks/use-mobile-detection';
import type { DeviceType, MobileContextProviderProps, MobileContextType, Orientation } from './types';
import { DEFAULT_BREAKPOINTS } from './types';

/**
 * Mobile Context for the Layout System Redesign
 *
 * This context extends the existing useMobileDetection functionality with:
 * - Comprehensive device classification (mobile/tablet/desktop)
 * - Viewport size tracking with debounced updates
 * - Orientation detection
 * - Performance-aware features
 * - Backwards compatibility with existing useMobileDetection hook
 */

// Create the context with default values
export const MobileContext = createContext<MobileContextType>({
  // Backwards compatible defaults
  isMobile: false,

  // Enhanced device classification
  deviceType: 'desktop',

  // Viewport information
  viewportWidth: 1200,
  viewportHeight: 800,
  orientation: 'landscape',

  // Performance features
  isLowMemoryDevice: false,
  preferReducedMotion: false,

  // Utility flags
  isPhone: false,
  isTablet: false,
  isDesktop: true,

  // Breakpoint utilities
  isExtraSmall: false,
  isSmall: false,
  isMedium: false,
  isLarge: true,
  isExtraLarge: false,
});

/**
 * Enhanced Mobile Context Provider
 *
 * Provides comprehensive mobile detection and device information throughout the app.
 * Built on top of existing useMobileDetection for backwards compatibility.
 */
export const MobileContextProvider: React.FC<MobileContextProviderProps> = ({
  children,
  debounceMs = 300,
  enablePerformanceMonitoring = true,
  breakpoints = {
    mobile: MOBILE_BREAKPOINT,
    tablet: DEFAULT_BREAKPOINTS.medium,
    desktop: DEFAULT_BREAKPOINTS.large,
  },
}) => {
  // State for viewport information
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1200));
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));

  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Performance monitoring
  const performanceMetrics = useRef({
    updateCount: 0,
    lastUpdateTime: Date.now(),
  });

  /**
   * Classify device type based on viewport width
   */
  const classifyDeviceType = useCallback(
    (width: number): DeviceType => {
      if (width <= breakpoints.mobile) {
        return 'mobile';
      } else if (width <= breakpoints.tablet) {
        return 'tablet';
      } else {
        return 'desktop';
      }
    },
    [breakpoints],
  );

  /**
   * Determine orientation from viewport dimensions
   */
  const getOrientation = useCallback((width: number, height: number): Orientation => {
    return width > height ? 'landscape' : 'portrait';
  }, []);

  /**
   * Detect low memory device characteristics
   */
  const detectLowMemoryDevice = useCallback((): boolean => {
    if (typeof navigator === 'undefined') return false;

    // Check for device memory API (Chrome)
    if ('deviceMemory' in navigator) {
      return (navigator as any).deviceMemory <= 4; // 4GB or less
    }

    // Fallback: estimate based on device type and user agent
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);

    return isMobileUA && viewportWidth <= breakpoints.mobile;
  }, [viewportWidth, breakpoints.mobile]);

  /**
   * Check for reduced motion preference
   */
  const checkReducedMotion = useCallback((): boolean => {
    if (typeof window === 'undefined') return false;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    return mediaQuery.matches;
  }, []);

  /**
   * Debounced viewport update handler
   */
  const handleViewportChange = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (typeof window !== 'undefined') {
        const newWidth = window.innerWidth;
        const newHeight = window.innerHeight;

        setViewportWidth(newWidth);
        setViewportHeight(newHeight);

        // Performance monitoring
        if (enablePerformanceMonitoring) {
          performanceMetrics.current.updateCount++;
          performanceMetrics.current.lastUpdateTime = Date.now();

          // Log performance data in development
          if (process.env.NODE_ENV === 'development') {
            console.debug('[MobileContext] Viewport updated:', {
              width: newWidth,
              height: newHeight,
              updateCount: performanceMetrics.current.updateCount,
            });
          }
        }
      }
    }, debounceMs);
  }, [debounceMs, enablePerformanceMonitoring]);

  /**
   * Setup viewport change listeners
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Initial viewport setup
    setViewportWidth(window.innerWidth);
    setViewportHeight(window.innerHeight);

    // Add resize listener
    window.addEventListener('resize', handleViewportChange);

    // Add orientation change listener for mobile devices
    window.addEventListener('orientationchange', handleViewportChange);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [handleViewportChange]);

  /**
   * Compute context value with memoization for performance
   */
  const contextValue = useMemo((): MobileContextType => {
    const deviceType = classifyDeviceType(viewportWidth);
    const isMobile = deviceType === 'mobile';
    const orientation = getOrientation(viewportWidth, viewportHeight);
    const isLowMemoryDevice = detectLowMemoryDevice();
    const preferReducedMotion = checkReducedMotion();

    return {
      // Backwards compatible mobile detection
      isMobile,

      // Enhanced device classification
      deviceType,

      // Viewport information
      viewportWidth,
      viewportHeight,
      orientation,

      // Performance features
      isLowMemoryDevice,
      preferReducedMotion,

      // Device type utilities
      isPhone: deviceType === 'mobile' && viewportWidth <= DEFAULT_BREAKPOINTS.extraSmall,
      isTablet: deviceType === 'tablet',
      isDesktop: deviceType === 'desktop',

      // Breakpoint utilities
      isExtraSmall: viewportWidth < DEFAULT_BREAKPOINTS.extraSmall,
      isSmall: viewportWidth >= DEFAULT_BREAKPOINTS.extraSmall && viewportWidth < DEFAULT_BREAKPOINTS.small,
      isMedium: viewportWidth >= DEFAULT_BREAKPOINTS.small && viewportWidth < DEFAULT_BREAKPOINTS.medium,
      isLarge: viewportWidth >= DEFAULT_BREAKPOINTS.medium && viewportWidth < DEFAULT_BREAKPOINTS.large,
      isExtraLarge: viewportWidth >= DEFAULT_BREAKPOINTS.large,
    };
  }, [viewportWidth, viewportHeight, classifyDeviceType, getOrientation, detectLowMemoryDevice, checkReducedMotion]);

  /**
   * Development debugging
   */
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && enablePerformanceMonitoring) {
      // Log context changes in development
      console.debug('[MobileContext] Context updated:', {
        deviceType: contextValue.deviceType,
        isMobile: contextValue.isMobile,
        viewport: `${contextValue.viewportWidth}x${contextValue.viewportHeight}`,
        orientation: contextValue.orientation,
      });
    }
  }, [contextValue, enablePerformanceMonitoring]);

  return <MobileContext.Provider value={contextValue}>{children}</MobileContext.Provider>;
};

/**
 * Export the context for direct access if needed
 */
// MobileContext already exported above at line 18
